import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getBatchCoverageDetail,
  getBatchHeader,
  getBatchInsights,
  getBatchInsightsStatus,
  getBatchLiveStage,
  getBatchOptimizationQuality,
  getCurationFilters,
  getCurationRunFilters,
  getNeedsAttentionCurationProducts,
  getNeedsAttentionGroups,
  getNeedsAttentionProducts,
  getProductDetail,
  getProductAttributeEdits,
  getProductEnrichment,
  getProductGapAnalysis,
  getProducts,
  getProductSearchDescriptors,
  refreshProductGapAnalysis,
  startInsightsRecompute,
} from '../services/catalog-operations.service';
import type {
  BatchHeader,
  BatchInsights,
  BatchStatus,
  ProductFilterValues,
  ProductTab,
} from '../types/catalog-operations.types';

import { pollInsightsRecomputeUntilSettled } from './use-insights-recompute';

/** Header statuses that mean the job is still moving — the window during which
 *  `/live-stage` carries live data worth polling (contract §5.2). Terminal
 *  statuses answer `204`, so polling stops. */
const NON_TERMINAL_STATUSES: ReadonlySet<BatchStatus> = new Set<BatchStatus>([
  'in-progress',
  'awaiting-approval',
  'delivering',
]);

/** Poll cadence for `/live-stage` and the recompute `/status` — the contract
 *  suggests 3–5s for both. */
export const BATCH_LIVE_STAGE_POLL_MS = 4000;
export const BATCH_INSIGHTS_STATUS_POLL_MS = 4000;

/** Poll cadence while a batch is actively delivering. Delivery settles
 *  server-side now that it's decoupled from the live workflow, so the detail
 *  page must re-fetch to observe DELIVERING → COMPLETED/FAILED. */
export const BATCH_DELIVERY_POLL_MS = 10000;
/** Cadence while the run is paused at the cost gate — a decision usually lands within minutes. */
const BATCH_COST_GATE_POLL_MS = 15000;
/** Faster cadence while waiting for a just-approved delivery's run id to appear. */
const DELIVERY_RUN_ID_POLL_MS = 5000;

/**
 * Phase 1 (shell) — fetches the batch header for the detail page, and the single
 * light source of truth for header fields (name, status, stages, connector,
 * `curationRefId`, run config, …) on every batch-scoped page: batch detail,
 * products, product detail, product siblings, and feed audit. Replaces the
 * former `useBatchDetail`, which fetched the heavy `/jobs/{id}` aggregate just
 * to read these same fields.
 *
 * Stays disabled without an id / when a caller only needs it conditionally
 * (`enabled`), so a page that mounts it speculatively doesn't issue a doomed
 * request. Always-stale and polls while delivering — status/stages move
 * server-side during delivery, so it must re-fetch to observe
 * DELIVERING → COMPLETED/FAILED without a hard reload.
 *
 * `pollForDeliveryRun` additionally re-fetches every 5 s until
 * `deliveryJobId` (the integration delivery-run id) is present — the approval
 * signal returns no run id and doesn't flip `isDelivering` synchronously, so a
 * screen that just approved needs this to learn which run to follow.
 *
 * `pollUntilEnriched` re-fetches every 10 s while the run is in progress and its `enriched`
 * stage hasn't completed — for a screen opened mid-run that shows something once enrichment ends.
 */
export function useBatchHeader(
  jobId: string,
  options?: { enabled?: boolean; pollForDeliveryRun?: boolean; pollUntilEnriched?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchHeader(jobId),
    queryFn: () => getBatchHeader(jobId),
    enabled: (options?.enabled ?? true) && Boolean(jobId),
    staleTime: 0,
    refetchInterval: (query) => {
      const header = query.state.data;
      if (options?.pollForDeliveryRun && !header?.deliveryJobId) return DELIVERY_RUN_ID_POLL_MS;
      const delivering =
        header?.isDelivering === true ||
        (header?.stages.some((s) => s.key === 'delivered' && s.state === 'active') ?? false);
      if (delivering) return BATCH_DELIVERY_POLL_MS;
      if (
        options?.pollUntilEnriched &&
        header?.status === 'in-progress' &&
        !header.stages.some((s) => s.key === 'enriched' && s.state === 'complete')
      ) {
        return BATCH_DELIVERY_POLL_MS;
      }
      // Paused at the cost gate: keep the header (status chip, costGate) fresh so the
      // waiting state clears once someone decides — in Slack or on another page.
      return header?.costGate ? BATCH_COST_GATE_POLL_MS : false;
    },
  });
}

/**
 * Phase 3 (coverage tabs) — fetches the Delivery Health + Enrichment Coverage
 * slice. Depends on the resolved `header` (a few derivations need header fields
 * the coverage payload omits), so it stays disabled until the header lands —
 * firing as soon as that fast call resolves. `enabled` lets the caller keep it
 * back further if needed.
 */
export function useBatchCoverageDetail(
  jobId: string,
  header: BatchHeader | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchCoverageDetail(jobId),
    queryFn: () => {
      // Never runs without a resolved header (see `enabled`); narrow explicitly
      // so neither a cast nor a non-null assertion is needed.
      if (!header) throw new Error('coverage read requires a resolved header');
      return getBatchCoverageDetail(jobId, header);
    },
    enabled: enabled && Boolean(jobId) && Boolean(header),
    staleTime: 0,
  });
}

/**
 * Phase 4 (heaviest, lazy) — fetches the Optimization Quality slice. Gated on
 * the tab being active so the long-pole request never runs on page load.
 */
export function useBatchOptimizationQuality(jobId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchOptimizationQuality(jobId),
    queryFn: () => getBatchOptimizationQuality(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
  });
}

/**
 * Phase 2 (live progress) — polls `/live-stage` while the job is running to
 * refresh per-stage counters without re-fetching the whole detail. Resolves
 * `null` on `204` (terminal/unreachable) and stops polling; the caller then
 * falls back to `header.stages`. `isRunning` is derived from the header status
 * so a terminal job never opens the poll at all.
 */
export function useBatchLiveStage(jobId: string, isRunning: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchLiveStage(jobId),
    queryFn: () => getBatchLiveStage(jobId),
    enabled: isRunning && Boolean(jobId),
    staleTime: 0,
    // Stop once the endpoint answers 204 (data === null) even if the header is
    // briefly still non-terminal — the workflow is gone, there's nothing to poll.
    refetchInterval: (query) => (query.state.data === null ? false : BATCH_LIVE_STAGE_POLL_MS),
  });
}

/** True when a header status means the job is still moving (so `/live-stage`
 *  is worth polling). Exported so the page derives the poll gate identically. */
export function isJobRunning(status: BatchStatus | undefined): boolean {
  return status !== undefined && NON_TERMINAL_STATUSES.has(status);
}

/**
 * Reads the AI-Performance recompute `/status` (refresh-safe). On mount it
 * re-reads `RECOMPUTING` from the server — so a page refresh mid-recompute
 * restores the spinner — and polls until the state leaves `RECOMPUTING`, then
 * invalidates the insights the tab renders. Returns `isServerRecomputing` for
 * the page to OR into its existing (integration-service) recompute spinner.
 * `workflowId` is the slug (`BatchHeader.batchId`), not the job uuid.
 */
export function useBatchInsightsServerStatus(workflowId: string, enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.catalogOperations.batchInsightsStatus(workflowId),
    queryFn: () => getBatchInsightsStatus(workflowId),
    enabled: enabled && Boolean(workflowId),
    staleTime: 0,
    refetchInterval: (q) =>
      q.state.data?.status === 'RECOMPUTING' ? BATCH_INSIGHTS_STATUS_POLL_MS : false,
  });

  const isServerRecomputing = query.data?.status === 'RECOMPUTING';
  // On the RECOMPUTING → settled transition the panels are fresh; refetch the
  // insights read. A ref (not derived state) so it fires once per transition.
  const wasRecomputingRef = useRef(false);
  useEffect(() => {
    if (isServerRecomputing) {
      wasRecomputingRef.current = true;
      return;
    }
    if (wasRecomputingRef.current) {
      wasRecomputingRef.current = false;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchInsights(workflowId),
      });
    }
  }, [isServerRecomputing, workflowId, queryClient]);

  return { isServerRecomputing };
}

/** How often `pollWhileEmpty` callers re-ask for the async insights rows. */
export const BATCH_INSIGHTS_POLL_MS = 5000;

/** True when an insights payload carries no ranked rows in any section — the
 *  shape the endpoint returns until the async ENRICHMENT_INSIGHTS callback
 *  fills it. Shared so a caller's "still waiting" state and the poll below
 *  always agree on what "empty" means. */
export function isBatchInsightsEmpty(insights: BatchInsights | undefined): boolean {
  return (
    !insights ||
    (insights.popularTerms.length === 0 &&
      insights.popularAttributes.length === 0 &&
      insights.occasions.length === 0 &&
      insights.trends.length === 0)
  );
}

/** Fetches the Performance Signals tab data for the given batch. `workflowId`
 *  is `BatchHeader.batchId` (the human-readable id), not the job uuid —
 *  callers must read it off an already-resolved `useBatchHeader` result.
 *  `enabled` lets the caller defer the request until both the tab is active
 *  and the workflow id has resolved, since it's a separate call from
 *  `useBatchHeader`. `pollWhileEmpty` re-asks until the first rows land; it is
 *  the caller's job to switch it back off once it has waited long enough,
 *  since a job that never produces rows would otherwise poll forever. */
export function useBatchInsights(workflowId: string, enabled = true, pollWhileEmpty = false) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchInsights(workflowId),
    queryFn: () => getBatchInsights(workflowId),
    enabled,
    // Insights are written by an async callback shortly AFTER the run COMPLETES,
    // so opt-in callers poll until the first ranked rows land, then stop.
    refetchInterval: (query) =>
      pollWhileEmpty && isBatchInsightsEmpty(query.state.data) ? BATCH_INSIGHTS_POLL_MS : false,
  });
}

/** Ceiling on the empty-insights self-heal: a run whose insights never populate
 *  and can't be recomputed (no connector) settles here rather than polling for the
 *  rest of the session. Mirrors the coverage budget. */
export const BATCH_INSIGHTS_POLL_BUDGET_MS = 120_000;

/** Empty insights responses to tolerate before auto-kicking a recompute. Fresh
 *  batches resolve `/insights` all-empty until the async ENRICHMENT_INSIGHTS
 *  callback fills it; one empty resolve is enough of a signal to recompute rather
 *  than sit on nothing. */
export const BATCH_INSIGHTS_EMPTY_POLLS_BEFORE_RECOMPUTE = 1;

interface BatchInsightsResult {
  insights: ReturnType<typeof useBatchInsights>['data'];
  isLoading: boolean;
  /** True while rows may still arrive — callers shimmer on this. Goes false once
   *  rows land, the auto-recompute settles, or the budget is spent. */
  isAwaitingRows: boolean;
  /** True while the one-shot auto-recompute is in flight — callers show the
   *  "recomputing" state rather than a bare shimmer. */
  isRecomputing: boolean;
}

/** `polling` → waiting on the async insights callback; `recomputing` → the
 *  one-shot auto-recompute is in flight; `settled` → stop, and let the insights
 *  read decide the UI (rows or empty). */
type InsightsAutoRecomputePhase = 'polling' | 'recomputing' | 'settled';

interface InsightsAutoRecomputeState {
  /** Workflow this state tracks; a different id resets everything. */
  id: string;
  /** Empty responses counted so far. */
  emptyPolls: number;
  /** `dataUpdatedAt` of the last counted response — dedupes re-renders so one
   *  fetch is only ever counted once. */
  countedAt: number;
  phase: InsightsAutoRecomputePhase;
}

function initialInsightsAutoRecomputeState(id: string): InsightsAutoRecomputeState {
  return { id, emptyPolls: 0, countedAt: 0, phase: 'polling' };
}

/**
 * `useBatchInsights` with a one-shot empty-insights self-heal. A fresh batch
 * resolves `/insights` all-empty (the async ENRICHMENT_INSIGHTS callback hasn't
 * populated it yet), so this reads insights once; if the resolved payload is
 * empty it fires the recompute **exactly once** (the same POST-then-poll the
 * manual button runs), surfaces `isRecomputing`, then re-reads. The
 * `polling → recomputing → settled` state machine (reconciled at render, guarded
 * by the workflow id) guarantees no re-trigger loop: whatever the re-read shows
 * — rows or still-empty — the phase is `settled` and it never fires again.
 *
 * Silent automatic recovery — no toasts (the manual button keeps those). A
 * missing `connectorCode` can't be recomputed, so that case falls back to the
 * plain bounded poll until the budget settles it. Manual recompute is unaffected.
 */
export function useBatchInsightsWithAutoRecompute(
  workflowId: string,
  connectorCode: string,
  enabled = true,
  /** Whether the empty payload is worth self-healing. Insights are derived from
   *  enrichment output, so the caller passes `false` while a run is still moving
   *  or failed before enrichment — empty is expected then, and there is nothing
   *  to recompute from. Insights are still FETCHED (for the top metric cards);
   *  only the auto-recompute is held back. */
  autoRecomputeEnabled = true,
): BatchInsightsResult {
  const queryClient = useQueryClient();
  const canRecompute = Boolean(connectorCode);

  // Per-workflow orchestration, reconciled at render so a new batch id starts
  // clean without waiting an extra effect tick.
  const [state, setState] = useState<InsightsAutoRecomputeState>(() =>
    initialInsightsAutoRecomputeState(workflowId),
  );
  if (state.id !== workflowId) setState(initialInsightsAutoRecomputeState(workflowId));
  const active = state.id === workflowId ? state : initialInsightsAutoRecomputeState(workflowId);

  // Ultimate ceiling: a run whose insights never populate and can't be recomputed
  // settles here instead of shimmering for the rest of the session.
  useEffect(() => {
    if (!workflowId || !enabled) return;
    const timer = setTimeout(() => {
      setState((s) => (s.id === workflowId ? { ...s, phase: 'settled' } : s));
    }, BATCH_INSIGHTS_POLL_BUDGET_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [workflowId, enabled]);

  // Poll-while-empty (and thus the whole self-heal) only runs when eligible; when
  // not, this behaves like a plain one-shot `useBatchInsights` read.
  const query = useBatchInsights(
    workflowId,
    enabled && Boolean(workflowId),
    autoRecomputeEnabled && active.phase !== 'settled',
  );
  const isEmpty = isBatchInsightsEmpty(query.data);

  // Count each empty resolve once. At the threshold, move to `recomputing` when a
  // connector is available; without one, stay in `polling` so the budget ceiling
  // (not the threshold) decides when to give up.
  useEffect(() => {
    if (!enabled || !workflowId || !autoRecomputeEnabled) return;
    if (!query.isSuccess || !isEmpty) return;
    setState((s) => {
      if (s.id !== workflowId || s.phase !== 'polling') return s;
      if (query.dataUpdatedAt === s.countedAt) return s;
      const emptyPolls = s.emptyPolls + 1;
      const reachedThreshold =
        canRecompute && emptyPolls >= BATCH_INSIGHTS_EMPTY_POLLS_BEFORE_RECOMPUTE;
      return {
        ...s,
        emptyPolls,
        countedAt: query.dataUpdatedAt,
        phase: reachedThreshold ? 'recomputing' : 'polling',
      };
    });
  }, [
    enabled,
    workflowId,
    autoRecomputeEnabled,
    query.isSuccess,
    query.dataUpdatedAt,
    isEmpty,
    canRecompute,
  ]);

  // Fire the recompute exactly once, on entering `recomputing`: POST, poll to a
  // terminal state, then re-read insights and settle. Whatever the outcome we
  // settle — rows show data, still-empty shows the empty state — so there is no
  // re-trigger loop.
  useEffect(() => {
    if (active.phase !== 'recomputing') return;
    void (async () => {
      try {
        const run = await startInsightsRecompute(workflowId, connectorCode);
        await pollInsightsRecomputeUntilSettled(run.runId);
      } catch {
        // Swallow: a failed/rejected auto-recompute just settles into the empty
        // state, same as a run that legitimately produced no insights.
      } finally {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.batchInsights(workflowId),
        });
        // The identity guard keeps this safe if the batch changed mid-run.
        setState((s) => (s.id === workflowId ? { ...s, phase: 'settled' } : s));
      }
    })();
  }, [active.phase, workflowId, connectorCode, queryClient]);

  return {
    insights: query.data,
    isLoading: query.isLoading,
    isAwaitingRows:
      Boolean(workflowId) &&
      enabled &&
      autoRecomputeEnabled &&
      isEmpty &&
      active.phase !== 'settled',
    isRecomputing: active.phase === 'recomputing',
  };
}

/** Fetches and caches products for a batch, scoped by tab. */
export function useProducts(batchId: string, tab: ProductTab) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchProducts(batchId, tab),
    queryFn: () => getProducts(batchId, tab),
  });
}

/** Fetches and caches the curation product detail. The endpoint keys on
 *  `productId` only — `batchId` is preserved for breadcrumb context but isn't
 *  part of the cache key.
 *
 *  Gated on a non-empty `productId`: the endpoint path is `products/{id}`, so an
 *  empty id requests `products/`, which the curation service answers 404
 *  ("Curation workflow not found"). Callers that mount before a product is
 *  chosen — the always-mounted quick-view dialog — would otherwise fire one
 *  doomed request per render. `enabled` additionally lets a caller hold the
 *  fetch back until its own UI is open. */
export function useProductDetail(batchId: string, productId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.curation.productDetail(productId),
    queryFn: () => getProductDetail(batchId, productId),
    enabled: enabled && !!productId,
  });
}

/** Fetches the enrichment payload (scores + field-level optimizations) for a
 *  single product. Mounted alongside `useProductDetail` on the detail page;
 *  the two queries hydrate independent sections. `enabled` lets callers skip
 *  the call entirely (used by the global needs-attention detail view, where
 *  products haven't been enriched yet). `connectorCode` scopes the call to the
 *  source connector (e.g. `META`, `GOOGLE`) — derived from the product's
 *  `feed_source` field. */
export function useProductEnrichment(
  productId: string,
  includeFailed = false,
  enabled = true,
  connectorCode?: string,
  /** Batch the product is viewed from; scopes the read to that batch's own row. */
  jobId?: string,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productEnrichment(
      productId,
      includeFailed,
      connectorCode,
      jobId,
    ),
    queryFn: () => getProductEnrichment(productId, includeFailed, connectorCode, jobId),
    enabled,
  });
}

/**
 * Fetches the provenance for every operator override on a product — who changed
 * which attribute, and when. Fired alongside `useProductEnrichment` rather than
 * behind an interaction, since the marker it drives sits on collapsed rows.
 *
 * Provenance only: the value each attribute currently carries comes from the
 * enrichment payload, so this never competes with it as a source of truth. The
 * endpoint never 404s — an unknown product and one with no edits both answer
 * `[]` — so there is deliberately no error branch downstream.
 */
export function useProductAttributeEdits(
  productId: string,
  enabled = true,
  connectorCode?: string,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productAttributeEdits(productId, connectorCode),
    queryFn: () => getProductAttributeEdits(productId, connectorCode),
    enabled,
  });
}

/** Poll cadence while the agent is working. The contract suggests ~2–3s; in-flight
 *  polls are status-only and don't re-invoke the agent. */
const GAP_POLL_START_MS = 2500;
/** Ceiling for the backoff, so a long analysis settles into a quiet poll. */
const GAP_POLL_MAX_MS = 15000;

/**
 * Fetches the content-delta gap analysis for a product. The endpoint is
 * read-through: a first read returns `PENDING` and starts the agent, so this
 * polls while the status is `PENDING`/`RUNNING` and stops once it reaches
 * `COMPLETED` or `FAILED`. Fired with the same `enabled` gate as
 * `useProductEnrichment` so it's already resolving before the user opens the tab.
 *
 * `COMPLETED` with a null summary is a real "no distinct attributes" answer and
 * must stop the poll — treating it as still-loading would poll forever.
 */
export function useProductGapAnalysis(productId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productGapAnalysis(productId),
    queryFn: () => getProductGapAnalysis(productId),
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status !== 'PENDING' && status !== 'RUNNING') return false;
      // Back off as the wait grows rather than hammering a slow analysis.
      const attempts = query.state.dataUpdateCount;
      return Math.min(GAP_POLL_START_MS * Math.max(1, attempts), GAP_POLL_MAX_MS);
    },
  });
}

/**
 * Forces a recompute for one product, then lets the poll in
 * `useProductGapAnalysis` take over. The 202 envelope comes back `PENDING`, so it
 * seeds the cache directly — that flips the query into its polling state without
 * waiting for the next scheduled fetch.
 */
export function useRefreshProductGapAnalysis(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => refreshProductGapAnalysis(productId),
    onSuccess: (pending) => {
      queryClient.setQueryData(queryKeys.catalogOperations.productGapAnalysis(productId), pending);
    },
    onError: (error) => {
      toast.error(extractApiError(error, 'Could not refresh gap analysis. Please try again.'));
    },
  });
}

/**
 * Fetches a product's search descriptors and phrase descriptions — one endpoint
 * serves both, so one query feeds both halves of the tab.
 *
 * Deliberately a plain query: unlike `useProductGapAnalysis` this endpoint is not
 * read-through. It reads persisted rows for the product's latest run, so there is
 * no lifecycle to poll and no refresh endpoint to expose. Fired with the same
 * `enabled` gate as `useProductEnrichment` so the data is resolved before the user
 * opens the tab.
 *
 * Resolves `null` for a product that never ran either step — see
 * `getProductSearchDescriptors`, which absorbs the contract's 404 so the empty
 * state and a genuine failure stay distinguishable.
 *
 * `enrichmentJobId` pins the payload to one enrichment run. Pass it wherever a
 * job is in scope: the data is producer-keyed, so the unpinned read answers with
 * the product's most recent run, which on a batch-scoped page can belong to a
 * different batch than the one being viewed.
 */
export function useProductSearchDescriptors(
  productId: string,
  enabled = true,
  enrichmentJobId?: string,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productSearchDescriptors(productId, enrichmentJobId),
    queryFn: () => getProductSearchDescriptors(productId, enrichmentJobId),
    enabled,
  });
}

/** Fetches the grouped Needs Attention items shown in the catalog drawer. */
export function useNeedsAttentionGroups(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.needsAttention(),
    queryFn: getNeedsAttentionGroups,
    enabled,
  });
}

/** Fetches the cross-batch list of products that need attention (full-page list view). */
export function useNeedsAttentionProducts() {
  return useQuery({
    queryKey: queryKeys.catalogOperations.needsAttentionProducts(),
    queryFn: getNeedsAttentionProducts,
  });
}

interface UseGlobalNeedsAttentionProductsParams {
  /** Optional connector code (e.g. `GOOGLE_MERCHANT`). Omit for true
   *  cross-connector mode — only overlap filters apply on the listing. */
  connectorCode?: string;
  /** 1-based page index. */
  page?: number;
  /** Page size. */
  size?: number;
  /** Drawer-applied filter values. `needs_attention` is implicit so callers
   *  should leave it unset. */
  filters?: ProductFilterValues;
  /** Free-text search committed by the user (Enter). */
  search?: string;
  /** Set false to skip the request — e.g. while the caller is still
   *  resolving product enrollment, or when no platform is live-enrolled and
   *  a "not enrolled" state renders instead. Defaults to true. */
  enabled?: boolean;
}

/** Live cross-batch needs-attention listing.
 *  Reads `subsidiary_id` from `auth.store` and stays disabled until it
 *  resolves so the call doesn't fire with a missing tenant.
 *  `keepPreviousData` keeps the table from flickering on pagination. */
export function useGlobalNeedsAttentionProducts({
  connectorCode,
  page = 1,
  size = 20,
  filters,
  search,
  enabled = true,
}: UseGlobalNeedsAttentionProductsParams = {}) {
  return useQuery({
    queryKey: queryKeys.curation.needsAttention(
      connectorCode ?? '',
      page,
      size,
      filters ?? {},
      search ?? '',
    ),
    queryFn: () =>
      getNeedsAttentionCurationProducts({
        ...(connectorCode ? { connectorCode } : {}),
        page,
        size,
        ...(filters ? { filters } : {}),
        ...(search ? { search } : {}),
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Fetches the filter definitions for the products drawer. `connectorCode`
 *  scopes the list to that connector; omit to fetch the overlap set. The
 *  hook is gated on subsidiary so it stays silent during auth bootstrap. */
export function useCurationFilters(connectorCode: string | undefined) {
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId);
  return useQuery({
    queryKey: queryKeys.curation.filters(subsidiaryId ?? '', connectorCode ?? ''),
    queryFn: () => getCurationFilters(connectorCode),
    enabled: Boolean(subsidiaryId),
  });
}

/** Fetches the filter definitions discovered from one run's products, for the
 *  per-batch products drawer. Disabled until both the run and its connector
 *  resolve — the endpoint rejects a connector that doesn't match the run. */
export function useCurationRunFilters(
  curationRefId: string | undefined,
  connectorCode: string | undefined,
) {
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId);
  return useQuery({
    queryKey: queryKeys.curation.runFilters(
      subsidiaryId ?? '',
      curationRefId ?? '',
      connectorCode ?? '',
    ),
    queryFn: () => getCurationRunFilters(curationRefId ?? '', connectorCode ?? ''),
    enabled: Boolean(subsidiaryId && curationRefId && connectorCode),
  });
}
